import { create } from "zustand";
import {
  mpDungeonsApi,
  type MpDungeonConfig,
  type MpDungeonRoomDetail,
  type MpDungeonRoomSummary,
  type MpDungeonRunState,
} from "../api/mpDungeons";
import { useGameStore } from "./useGameStore";

interface MpDungeonState {
  config: MpDungeonConfig | null;
  myRoom: MpDungeonRoomDetail | null;
  rooms: MpDungeonRoomSummary[];
  run: MpDungeonRunState | null;
  loading: boolean;
  error: string | null;
  expiredModal: string | null;
  lastExpiredKey: string | null;

  loadConfig: () => Promise<void>;
  refreshRooms: () => Promise<void>;
  poll: () => Promise<void>;
  createRoom: (level: number, difficulty: string) => Promise<void>;
  joinRoom: (roomId: string) => Promise<void>;
  leaveRoom: () => Promise<void>;
  setReady: (ready: boolean) => Promise<void>;
  startDungeon: () => Promise<void>;
  kickMember: (targetUserId: number) => Promise<void>;
  moveToNode: (nodeId: string) => Promise<string[]>;
  ackBattle: () => Promise<string[]>;
  abandon: () => Promise<void>;
  dismissExpiredModal: () => void;
}

export const useMpDungeonStore = create<MpDungeonState>((set, get) => ({
  config: null,
  myRoom: null,
  rooms: [],
  run: null,
  loading: false,
  error: null,
  expiredModal: null,
  lastExpiredKey: null,

  loadConfig: async () => {
    try {
      const cfg = await mpDungeonsApi.getConfig();
      set({ config: cfg });
    } catch {
      // non-fatal
    }
  },

  refreshRooms: async () => {
    try {
      const res = await mpDungeonsApi.listRooms();
      set({ rooms: res.rooms });
    } catch {
      // non-fatal
    }
  },

  poll: async () => {
    const { lastExpiredKey } = get();
    try {
      const me = await mpDungeonsApi.getMe();
      const game = useGameStore.getState();

      if (me.status === "expired") {
        const key = me.expired_message ?? "expired";
        set({ myRoom: null, run: null });
        if (lastExpiredKey === key) return;
        set({ lastExpiredKey: key });
        if (me.expired_message) {
          if (game.activeScreen === "mpDungeons") {
            game.pushInfoMessages([me.expired_message]);
          } else {
            set({ expiredModal: me.expired_message });
          }
        }
        return;
      }

      if (me.status === "waiting" && me.room) {
        set({ myRoom: me.room, run: null, lastExpiredKey: null });
        return;
      }

      if (me.status === "running" && me.run) {
        set({ run: me.run, myRoom: null, lastExpiredKey: null });
        if (game.activeScreen !== "mpDungeonRun") {
          game.setScreen("mpDungeonRun");
        }
        return;
      }

      if ((me.status === "completed" || me.status === "failed") && me.run) {
        set({ run: me.run, myRoom: null });
        return;
      }

      set({ myRoom: null, run: null });
    } catch {
      // ignore poll errors
    }
  },

  createRoom: async (level, difficulty) => {
    set({ loading: true, error: null });
    try {
      const res = await mpDungeonsApi.createRoom(level, difficulty);
      set({ myRoom: res.room, loading: false });
      await get().refreshRooms();
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : "Failed to create room" });
    }
  },

  joinRoom: async (roomId) => {
    set({ loading: true, error: null });
    try {
      const me = await mpDungeonsApi.joinRoom(roomId);
      set({ myRoom: me.room ?? null, loading: false });
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : "Failed to join" });
    }
  },

  leaveRoom: async () => {
    const { myRoom } = get();
    if (!myRoom) return;
    set({ loading: true, error: null });
    try {
      await mpDungeonsApi.leaveRoom(myRoom.room_id);
      set({ myRoom: null, loading: false });
      await get().refreshRooms();
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : "Failed to leave" });
    }
  },

  setReady: async (ready) => {
    const { myRoom } = get();
    if (!myRoom) return;
    set({ loading: true, error: null });
    try {
      const me = await mpDungeonsApi.setReady(myRoom.room_id, ready);
      set({ myRoom: me.room ?? null, loading: false });
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : "Failed" });
    }
  },

  startDungeon: async () => {
    const { myRoom } = get();
    if (!myRoom) return;
    set({ loading: true, error: null });
    try {
      const me = await mpDungeonsApi.startDungeon(myRoom.room_id);
      set({ run: me.run ?? null, myRoom: null, loading: false });
      useGameStore.getState().setScreen("mpDungeonRun");
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : "Failed to start" });
    }
  },

  kickMember: async (targetUserId) => {
    const { myRoom } = get();
    if (!myRoom) return;
    set({ loading: true, error: null });
    try {
      const me = await mpDungeonsApi.kickMember(myRoom.room_id, targetUserId);
      set({ myRoom: me.room ?? null, loading: false });
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : "Failed to kick player" });
    }
  },

  moveToNode: async (nodeId) => {
    set({ loading: true, error: null });
    try {
      const res = await mpDungeonsApi.move(nodeId);
      set({ run: res.run, loading: false });
      if (res.info_messages?.length) {
        useGameStore.getState().pushInfoMessages(res.info_messages);
      }
      await useGameStore.getState().refreshHero();
      await useGameStore.getState().refreshBalance();
      return res.info_messages ?? [];
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : "Move failed" });
      return [];
    }
  },

  ackBattle: async () => {
    try {
      const res = await mpDungeonsApi.ackBattle();
      set({ run: res.run });
      if (res.info_messages?.length) {
        useGameStore.getState().pushInfoMessages(res.info_messages);
      }
      if (res.run.status === "completed" || res.run.status === "failed") {
        set({ run: null });
        useGameStore.getState().setScreen("mpDungeons");
      }
      await useGameStore.getState().refreshHero();
      await useGameStore.getState().refreshBalance();
      return res.info_messages ?? [];
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Ack failed" });
      return [];
    }
  },

  abandon: async () => {
    set({ loading: true, error: null });
    try {
      await mpDungeonsApi.abandon();
      set({ run: null, myRoom: null, loading: false });
      useGameStore.getState().setScreen("mpDungeons");
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : "Abandon failed" });
    }
  },

  dismissExpiredModal: () => set({ expiredModal: null }),
}));
